import {
  ReportSection,
  ReportSectionItemContent,
  ScalingButtonGroup,
} from '@/components';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import type { LedgerPaymentSectionProps } from '@/features/mold/models/ledger';
import {
  getContractAmountOrExpendituresTitle,
  getContractName,
  getPaymentAmountBreakdownTableData,
  paymentValueConverter,
  shouldShowPaymentGraph,
} from '@/features/project/_utils/payment';
import { isBefore2025 } from '@/features/project/_utils/project';
import type { PaymentType } from '@/features/project/projects/_models/payment';
import { useLocaledAmount } from '@/providers';
import { FALLBACK_TEXT } from '@/utils';
import classNames from 'classnames';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from 'reactflow';
import {
  LEDGER_BORDER,
  LEDGER_RIGHT_ALIGNED_STYLE,
  type TableRowType,
  formatBidRate,
  getEmptyTableDataByHeader,
} from '../../../../utils';
import { PaymentGraphLayout } from '../../../flow';
import styles from '../LedgerPrint.module.scss';
import { TableContent } from '../TableContent';
import { AmountBreakdownTable } from './AmountBreakdownTable';
import { PaymentGroupTable } from './PaymentGroupTable';

const printHiddenClass = classNames(styles.hidden_print);

export const PaymentSection: FC<LedgerPaymentSectionProps> = ({
  payments,
  paymentGroups,
  paymentGraph,
  project,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { devtoolState } = useDevToolsStorage();

  const isKsStandardAfter2025 = useMemo(
    () =>
      project?.sheetType === 'KS' &&
      project.fundAdditionalAttributes?.fundSheetType === 'standard' &&
      !isBefore2025(project),
    [project],
  );

  const paymentTableData = useMemo<TableRowType>(() => {
    const topHeader: TableRowType[number] = {
      isHeader: true,
      row: [
        {
          isSpace: true,
          ratio: 0.2,
          cellStyle: { borderLeft: 'none' },
        },
        { text: t('contractor-name'), ratio: 2 },
        {
          textWrapperStyle: { justifyContent: 'center' },
          text: t('contract-amount'),
          ratio: 0.6,
        },
        { text: t('corporate-number'), ratio: 2.4 },
      ],
    };

    const getContractHeader = (
      paymentType: PaymentType,
    ): TableRowType[number] => ({
      isHeader: true,
      row: [
        {
          isSpace: true,
          ratio: 0.2,
          cellStyle: {
            borderBottom: 0,
            background: 'white',
            borderLeft: 'none',
          },
        },
        {
          isSpace: true,
          ratio: 0.1,
          cellStyle: { borderBottom: 0, background: 'white' },
        },
        {
          text: t('table-payment-contract-overview-label'),
          ratio: 1.9,
          cellStyle: { borderTop: LEDGER_BORDER },
        },
        {
          text: getContractAmountOrExpendituresTitle(paymentType),
          ratio: !isKsStandardAfter2025 ? 0.6 : 3,
          cellStyle: { borderTop: LEDGER_BORDER },
          textWrapperStyle: { justifyContent: 'center' },
        },
        {
          text: t('number-of-applicants-table-title'),
          ratio: 0.4,
          cellStyle: { borderTop: LEDGER_BORDER },
          textWrapperStyle: { justifyContent: 'center' },
        },
        {
          text: t('bid-rate'),
          ratio: 0.4,
          cellStyle: { borderTop: LEDGER_BORDER },
          textWrapperStyle: { justifyContent: 'center' },
        },
        {
          text: t('payment-reason-title'),
          ratio: 1.6,
          cellStyle: { borderTop: LEDGER_BORDER },
        },
      ],
    });

    const data = payments?.flatMap<TableRowType[number]>((data, dataIdx) => {
      const isLastDataIdx = dataIdx === payments.length - 1;
      const contractCount = data.contracts.length;
      const borderBottom =
        contractCount >= 1 || isLastDataIdx ? 'none' : LEDGER_BORDER;
      const blockBody: TableRowType = [
        {
          row: [
            {
              textWrapperStyle: {
                justifyContent: 'center',
                minHeight: '3em',
              },
              cellStyle: {
                borderBottom,
                borderLeft: 'none',
              },
              text: paymentGroups?.find(
                (paymentGroup) => paymentGroup.id === data.paymentGroupId,
              )?.displayCode,
            },
            {
              text: data.name,
              cellStyle: { borderBottom },
            },
            {
              cellStyle: { borderBottom },
              textWrapperStyle: LEDGER_RIGHT_ALIGNED_STYLE,
              text: paymentValueConverter(
                data.totalContractAmount,
                unit,
                !!devtoolState?.isDebugMode,
                data.negativeTotalContractAmountCount,
              ),
            },
            { text: data.corporateNumber, cellStyle: { borderBottom } },
          ].map((v, idx) => ({ ...v, ratio: topHeader.row[idx]?.ratio })),
        },
      ];

      const contractData = ((): TableRowType => {
        return data.contracts.flatMap<TableRowType[number]>(
          (contract, contractIdx) => {
            const isLast = contractIdx === data.contracts.length - 1;
            const header = getContractHeader(data.type);

            return [
              ...(contractIdx === 0 ? [header] : []),
              {
                row: [
                  {
                    isSpace: true,
                    cellStyle: {
                      borderBottom:
                        isLast && !isLastDataIdx ? LEDGER_BORDER : 0,
                      borderLeft: 'none',
                    },
                  },
                  {
                    isSpace: true,
                    cellStyle: {
                      borderBottom:
                        isLast && !isLastDataIdx ? LEDGER_BORDER : 0,
                    },
                  },
                  {
                    text: `${contract.overview}\n${
                      getContractName(
                        contract.contractMethod,
                        contract.contractMethodDescription,
                      ) ?? FALLBACK_TEXT
                    } `,
                  },
                  {
                    textWrapperStyle: LEDGER_RIGHT_ALIGNED_STYLE,
                    text: paymentValueConverter(
                      contract.amount,
                      unit,
                      false,
                      undefined,
                    ),
                  },
                  {
                    text: contract.numberOfApplicants,
                    textWrapperStyle: LEDGER_RIGHT_ALIGNED_STYLE,
                  },
                  {
                    text: formatBidRate(contract.bidRate),
                    textWrapperStyle: LEDGER_RIGHT_ALIGNED_STYLE,
                  },
                  { text: contract.reason },
                ].map((v, idx) => ({ ...v, ratio: header.row[idx]?.ratio })),
              },
            ];
          },
        );
      })();
      return [topHeader, ...blockBody, ...contractData];
    });

    return data?.length ? data : getEmptyTableDataByHeader(topHeader);
  }, [paymentGroups, payments, t, unit, isKsStandardAfter2025, devtoolState]);

  if (!project || !paymentGroups || !paymentGraph) {
    return null;
  }

  const isShowPaymentGraph = !!(
    project.fiscalYear &&
    shouldShowPaymentGraph(project.fiscalYear, project.sheetType)
  );
  const paymentAmountBreakdownTableData = getPaymentAmountBreakdownTableData(
    paymentGroups ?? [],
  );

  return (
    <ReportSection
      title={t('payment')}
      items={[
        ...(isShowPaymentGraph
          ? [
              {
                wrapperStyle: { borderLeft: 'none' },
                item: (
                  <ReportSectionItemContent
                    wrapperStyle={{
                      flexDirection: 'column',
                      borderBottom: 'none',
                      pageBreakAfter: 'always',
                    }}
                    labelStyle={{
                      borderLeft: 'none',
                      width: '100%',
                      borderRight: 'none',
                      borderBottom: LEDGER_BORDER,
                    }}
                    label={t('payment-edge')}
                    value={
                      <PaymentGraphLayout
                        hideBorder
                        projectId={project.id}
                        ministryId={project.ministryId}
                        paymentIndirectCosts={project.paymentIndirectCosts}
                        paymentGroups={paymentGroups}
                        paymentNodesMeta={paymentGraph.nodesMeta}
                        paymentEdges={paymentGraph.edges}
                        panelNode={() => {
                          return (
                            <Panel
                              className={printHiddenClass}
                              position="top-right"
                            >
                              <ScalingButtonGroup />
                            </Panel>
                          );
                        }}
                      />
                    }
                  />
                ),
              },
            ]
          : []),
        {
          subtitle: isKsStandardAfter2025
            ? t(
                'ledger-payment-list-with-unit-expenditure-to-each-block-in-previous-year',
              )
            : t('ledger-payment-list-with-unit'),

          item: (
            <ReportSectionItemContent
              isTable
              isLast
              hasWrapperFlex={!!paymentGroups.length}
              wrapperStyle={
                // グラフがなく量が多いと、タイトルだけ残して改ページすることがあったため
                isShowPaymentGraph ? undefined : { breakInside: 'avoid-column' }
              }
              value={
                <PaymentGroupTable
                  paymentGroups={paymentGroups}
                  isKsStandardAfter2025={isKsStandardAfter2025}
                />
              }
            />
          ),
        },
        {
          subtitle: isKsStandardAfter2025
            ? t(
                'payment-amount-breakdown-expenditure-from-each-block-in-previous-year-with-unit',
              )
            : t('payment-amount-breakdown-with-unit'),
          item: (
            <ReportSectionItemContent
              isTable
              isLast
              // NOTE: 2025年以降KSの帳票でデータがない場合に線が途切れることを防ぐために実装。空の場合１行は表示するため最小は１
              hasWrapperFlex={
                paymentAmountBreakdownTableData.length
                  ? // 見出しの文言が長く、heightが高いため、２カラム以下はレイアウト崩れる
                    paymentAmountBreakdownTableData.length > 2
                  : false
              }
              value={
                <AmountBreakdownTable
                  paymentAmountBreakdownTableData={
                    paymentAmountBreakdownTableData
                  }
                />
              }
            />
          ),
        },
        ...(project.sheetType === 'KS'
          ? []
          : [
              {
                subtitle: t('ledger-payment-contractor-list-with-unit'),
                item: (
                  <TableContent defaultDataLength={3} data={paymentTableData} />
                ),
              },
            ]),
      ]}
    />
  );
};
